import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  SecondStageScreeningService,
  SecondStageConflict,
} from '../../../services/second-stage-screening.service';
import { ProjectService } from '../../../services/project.service';
import { GroupService } from '../../../services/group.service';
import { BaseAlertComponent } from '../../alerts/base-alert/base-alert.component';
import { ProjectState } from '../../../dtos/project-state';
import { Project } from '../../../dtos/project';
import { Group } from '../../../dtos/group';
import { Globals } from '../../../global/globals';
import { httpErrorToMessage } from '../../../project-corpus/adapters/http-error-message';
import { InMemoryPdfSource } from '../../../claim-annotator/adapters/in-memory-pdf-source';
import { PDF_SOURCE } from '../../../claim-annotator/ports/pdf-source.port';
import { PdfViewerComponent } from '../project-annotator/pdf-viewer.component';

interface ConflictRow extends SecondStageConflict {
  expanded: boolean;
  resolving: boolean;
  selectedDecision: 'accept1' | 'accept2' | 'custom' | null;
  customInclude: boolean | null;
  customExclusionReason: string;
  resolved: boolean;
}

@Component({
  selector: 'app-project-conflict-resolution',
  standalone: true,
  imports: [CommonModule, FormsModule, BaseAlertComponent, PdfViewerComponent],
  providers: [InMemoryPdfSource, { provide: PDF_SOURCE, useExisting: InMemoryPdfSource }],
  templateUrl: './project-conflict-resolution.component.html',
  styleUrls: ['./project-conflict-resolution.component.scss'],
})
export class ProjectConflictResolutionComponent implements OnInit, OnDestroy {
  private pdfSource = inject(InMemoryPdfSource);
  projectId = '';
  project: Project | null = null;
  group: Group | null = null;
  projectState: ProjectState = ProjectState.FULL_TEXT_SCREENING;
  projectCreatedAt = '';
  projectCompletedAt = '';
  readonly ProjectState = ProjectState;

  conflicts: ConflictRow[] = [];
  exclusionReasons: string[] = [];
  loading = false;
  errorMessage = '';
  successMessage = '';
  piOnlyNotice = '';

  // PDF modal state
  pdfModalConflict: ConflictRow | null = null;
  pdfSrc: SafeResourceUrl | null = null;
  pdfLoading = false;
  pdfError = '';
  private pdfBlobUrl: string | null = null;

  get unresolvedCount(): number {
    return this.conflicts.filter((c) => !c.resolved).length;
  }

  get resolvedCount(): number {
    return this.conflicts.filter((c) => c.resolved).length;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    protected location: Location,
    private globals: Globals,
    private screeningService: SecondStageScreeningService,
    private projectService: ProjectService,
    private groupService: GroupService,
  ) {}

  ngOnInit(): void {
    this.projectId = this.route.parent?.snapshot.paramMap.get('projectId') || '';
    if (this.projectId) {
      this.loadProjectDetails();
    }
  }

  loadProjectDetails(): void {
    this.projectService.getProjectById(this.projectId).subscribe({
      next: (project) => {
        if (project) {
          this.project = project;
          this.projectState = project.state as ProjectState;
          this.projectCreatedAt = project.createdAt || '';
          this.projectCompletedAt = project.completedAt || '';
          this.groupService.getGroupById(this.project?.groupId).subscribe({
            next: (group) => {
              this.group = group;
            },
            error: (err: any) => {
              console.error('Failed to load group details', err);
            },
          });
          if (project.userRole !== 'PI') {
            this.piOnlyNotice = "This page is available to the project's PI only.";
            return;
          }
          this.loadConflicts();
          this.loadExclusionReasons();
        }
      },
      error: (err: any) => {
        console.error('Failed to load project details', err);
      },
    });
  }

  loadConflicts(): void {
    this.loading = true;
    this.errorMessage = '';
    this.screeningService.getConflicts(this.projectId).subscribe({
      next: (conflicts: SecondStageConflict[]) => {
        this.conflicts = conflicts.map((c) => ({
          ...c,
          expanded: false,
          resolving: false,
          selectedDecision: null,
          customInclude: null,
          customExclusionReason: '',
          resolved: false,
        }));
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Failed to load conflicts', err);
        this.errorMessage = 'Failed to load conflicts. Please try again.';
        this.loading = false;
      },
    });
  }

  loadExclusionReasons(): void {
    this.projectService.getExclusionReasons(this.projectId).subscribe({
      next: (reasons: string[]) => {
        this.exclusionReasons = reasons;
      },
      error: (err: any) => {
        console.error('Failed to load exclusion reasons', err);
      },
    });
  }

  toggleRow(conflict: ConflictRow): void {
    if (conflict.resolved) return;
    conflict.expanded = !conflict.expanded;
  }

  selectDecision(conflict: ConflictRow, decision: 'accept1' | 'accept2' | 'custom'): void {
    conflict.selectedDecision = decision;
    conflict.customInclude = null;
    conflict.customExclusionReason = '';
  }

  canSubmit(conflict: ConflictRow): boolean {
    if (!conflict.selectedDecision) return false;
    if (conflict.selectedDecision === 'custom') {
      if (conflict.customInclude === null) return false;
      if (!conflict.customInclude && !conflict.customExclusionReason.trim()) return false;
    }
    return true;
  }

  submitResolution(conflict: ConflictRow): void {
    if (!this.canSubmit(conflict) || conflict.resolving) return;

    let include: boolean;
    let exclusionReason: string | undefined;

    if (conflict.selectedDecision === 'accept1') {
      include = conflict.decision1.includeDecision;
      exclusionReason = conflict.decision1.exclusionReason ?? undefined;
    } else if (conflict.selectedDecision === 'accept2') {
      include = conflict.decision2.includeDecision;
      exclusionReason = conflict.decision2.exclusionReason ?? undefined;
    } else {
      include = conflict.customInclude!;
      exclusionReason = include ? undefined : conflict.customExclusionReason.trim();
    }

    conflict.resolving = true;
    this.errorMessage = '';

    this.screeningService
      .resolveConflict(this.projectId, conflict.paperToProjectId, include, exclusionReason)
      .subscribe({
        next: () => {
          conflict.resolved = true;
          conflict.expanded = false;
          conflict.resolving = false;
          this.successMessage = `Conflict resolved for "${conflict.title}".`;
          setTimeout(() => (this.successMessage = ''), 4000);
        },
        error: (err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 401) {
            this.router.navigate(['/login']);
            return;
          }
          this.errorMessage = httpErrorToMessage(err);
          conflict.resolving = false;
        },
      });
  }

  // ── PDF Modal ─────────────────────────────────────────────────────────────

  async openPdfModal(conflict: ConflictRow): Promise<void> {
    this.pdfModalConflict = conflict;
    this.pdfSrc = null;
    this.pdfError = '';
    this.pdfLoading = true;
    this.pdfSource.clear();
    this.revokeBlobUrl();

    try {
      if (conflict.pdfId != null && this.project?.groupId) {
        const url = `${this.globals.backendUri}/groups/${this.project.groupId}/pdfs/${conflict.pdfId}/content`;
        const blob = await firstValueFrom(this.http.get(url, { responseType: 'blob' }));
        this.pdfBlobUrl = URL.createObjectURL(blob);
        this.pdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBlobUrl);
        this.pdfSource.setBlob(blob, conflict.title || `${conflict.pdfId}.pdf`);
      } else if (conflict.pdfUrl && this.project?.groupId) {
        // A cross-origin URL would be CORS-blocked by the viewer's pdf.js fetch. Materialize it
        // on the backend (idempotent server-side download) and serve the bytes same-origin.
        const gid = this.project.groupId;
        const matUrl = `${this.globals.backendUri}/groups/${gid}/papers/${conflict.paperId}/pdf`;
        const { pdfId } = await firstValueFrom(this.http.post<{ pdfId: number }>(matUrl, null));
        const contentUrl = `${this.globals.backendUri}/groups/${gid}/pdfs/${pdfId}/content`;
        const blob = await firstValueFrom(this.http.get(contentUrl, { responseType: 'blob' }));
        this.pdfBlobUrl = URL.createObjectURL(blob);
        this.pdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBlobUrl);
        this.pdfSource.setBlob(blob, conflict.title || `${conflict.paperId}.pdf`);
      } else {
        this.pdfError = 'No PDF is available for this paper.';
      }
    } catch (e) {
      this.pdfError =
        e instanceof HttpErrorResponse
          ? httpErrorToMessage(e)
          : 'Failed to load the PDF. Please try again.';
    } finally {
      this.pdfLoading = false;
    }
  }

  closePdfModal(): void {
    this.pdfModalConflict = null;
    this.pdfSrc = null;
    this.pdfError = '';
    this.pdfSource.clear();
    this.revokeBlobUrl();
  }

  private revokeBlobUrl(): void {
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
      this.pdfBlobUrl = null;
    }
  }

  backToScreening(): void {
    void this.router.navigate(['/projects', this.projectId, 'full-text-screening']);
  }

  ngOnDestroy(): void {
    this.revokeBlobUrl();
  }

  // ──────────────────────────────────────────────────────────────────────────
}
